import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { useConfirm } from "@/hooks/use-confirm";
import { ApiError } from "@/lib/api";
import { DatasheetPicker, type DatasheetPickerValue } from "@/components/datasheet-picker";
import { ArtworkThumbnail } from "@/components/artwork-thumbnail";
import {
  useArtworkTemplate, useCreateArtworkTemplate, useUpdateArtworkTemplate, useUploadArtworkTemplateImage,
  useDeleteArtworkTemplate, type ArtworkTemplateFieldInput,
} from "@/lib/artwork-templates";

interface FieldRow { fieldName: string; maxCharacters: string; widthMm: string; }
const emptyField = (): FieldRow => ({ fieldName: "", maxCharacters: "", widthMm: "" });

const toInputs = (rows: FieldRow[]): ArtworkTemplateFieldInput[] =>
  rows.map((r) => ({
    fieldName: r.fieldName,
    maxCharacters: r.maxCharacters === "" ? null : Number(r.maxCharacters),
    widthMm: r.widthMm === "" ? null : Number(r.widthMm),
  }));

export function ArtworkTemplateDetailPage() {
  const { id } = useParams();
  const isCreate = id === undefined;
  const templateId = isCreate ? 0 : Number(id);
  const navigate = useNavigate();
  const { data: existing } = useArtworkTemplate(templateId);
  const { has } = usePermissions();
  const confirm = useConfirm();
  const create = useCreateArtworkTemplate();
  const update = useUpdateArtworkTemplate(templateId);
  const uploadImage = useUploadArtworkTemplateImage(templateId);
  const del = useDeleteArtworkTemplate();
  const queryClient = useQueryClient();
  const [bust, setBust] = useState(0);

  async function onPickImage(file: File | undefined) {
    if (!file) return;
    try {
      await uploadImage.mutateAsync(file);
      setBust((b) => b + 1);
      void queryClient.invalidateQueries({ queryKey: ["file-thumbnail"] });
      toast.success("Image uploaded");
    } catch {
      toast.error("Could not upload the image");
    }
  }

  const [datasheet, setDatasheet] = useState<DatasheetPickerValue | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [prepaidSize, setPrepaidSize] = useState("1");
  const [multiple, setMultiple] = useState("1");
  const [heightMm, setHeightMm] = useState("");
  const [widthMm, setWidthMm] = useState("");
  const [rows, setRows] = useState<FieldRow[]>([emptyField()]);
  const [hydrated, setHydrated] = useState(false);

  if (!isCreate && existing && !hydrated) {
    setName(existing.name);
    setDescription(existing.description ?? "");
    setPrepaidSize(String(existing.prepaidSize));
    setMultiple(String(existing.prepaidSizeMultiple));
    setHeightMm(existing.heightMm?.toString() ?? "");
    setWidthMm(existing.widthMm?.toString() ?? "");
    setRows(existing.fields.length
      ? existing.fields.map((f) => ({ fieldName: f.fieldName, maxCharacters: f.maxCharacters?.toString() ?? "", widthMm: f.widthMm?.toString() ?? "" }))
      : [emptyField()]);
    setDatasheet({ datasheetId: existing.datasheetId, label: [existing.companyName, existing.datasheetTypeDescription].filter(Boolean).join(" — ") });
    setHydrated(true);
  }

  const setRow = (i: number, patch: Partial<FieldRow>) => setRows((p) => p.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const canSubmit = name.trim() !== "" && (isCreate ? datasheet !== null : true)
    && prepaidSize !== "" && multiple !== "" && rows.some((r) => r.fieldName.trim() !== "");

  async function handleDelete() {
    const ok = await confirm({ title: "Delete template?", body: name || `Template #${templateId}`, confirmLabel: "Delete", intent: "destructive" });
    if (!ok) return;
    try {
      await del.mutateAsync({ id: templateId, rowVersion: existing?.rowVersionBase64 ?? null });
      toast.success("Template deleted");
      void navigate("/admin/artwork-templates");
    } catch (e) {
      const detail = e instanceof ApiError ? (e.body as { detail?: string } | null)?.detail : null;
      toast.error(detail ?? "Could not delete the template");
    }
  }

  async function submit() {
    try {
      const fields = toInputs(rows.filter((r) => r.fieldName.trim() !== ""));
      const scalars = {
        name, description: description || null, prepaidSize: Number(prepaidSize),
        prepaidSizeMultiple: Number(multiple),
        heightMm: heightMm === "" ? null : Number(heightMm),
        widthMm: widthMm === "" ? null : Number(widthMm), fields,
      };
      if (isCreate) {
        if (!datasheet) return;
        const created = await create.mutateAsync({ datasheetId: datasheet.datasheetId, ...scalars });
        toast.success("Template created");
        void navigate(`/admin/artwork-templates/${created.artworkTemplateId}`);
      } else {
        await update.mutateAsync({ body: scalars, rowVersion: existing?.rowVersionBase64 ?? null });
        toast.success("Template saved");
      }
    } catch (e) {
      const detail = e instanceof ApiError ? (e.body as { detail?: string } | null)?.detail : null;
      toast.error(detail ?? "Could not save the template");
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-6">
      <h1 className="text-xl font-semibold">{isCreate ? "New artwork template" : "Edit artwork template"}</h1>

      <div className="space-y-1">
        <Label htmlFor="awt-name">Name</Label>
        <Input id="awt-name" aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>

      {isCreate ? (
        <div className="space-y-1">
          <Label>Datasheet</Label>
          <DatasheetPicker value={datasheet} onChange={setDatasheet} />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Datasheet: {datasheet?.label ?? "—"}</p>
      )}

      <div className="space-y-1">
        <Label htmlFor="awt-desc">Description</Label>
        <Input id="awt-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>

      <div className="flex gap-3">
        <div className="space-y-1">
          <Label htmlFor="awt-size">Prepaid size (1–7)</Label>
          <Input id="awt-size" type="number" min={1} max={7} className="w-28" value={prepaidSize} onChange={(e) => setPrepaidSize(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="awt-mult">Size multiple</Label>
          <Input id="awt-mult" type="number" className="w-28" value={multiple} onChange={(e) => setMultiple(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="awt-height">Height mm</Label>
          <Input id="awt-height" type="number" className="w-28" value={heightMm} onChange={(e) => setHeightMm(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="awt-width">Width mm</Label>
          <Input id="awt-width" type="number" className="w-28" value={widthMm} onChange={(e) => setWidthMm(e.target.value)} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Fields</Label>
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input aria-label={`Field ${i + 1} name`} placeholder="Field name" value={r.fieldName} onChange={(e) => setRow(i, { fieldName: e.target.value })} />
            <Input aria-label={`Field ${i + 1} max characters`} type="number" className="w-28" placeholder="Max" value={r.maxCharacters} onChange={(e) => setRow(i, { maxCharacters: e.target.value })} />
            <Input aria-label={`Field ${i + 1} width`} type="number" className="w-24" placeholder="Width mm" value={r.widthMm} onChange={(e) => setRow(i, { widthMm: e.target.value })} />
            {rows.length > 1 ? (
              <Button type="button" variant="ghost" size="sm" aria-label="Remove field" onClick={() => setRows((p) => p.filter((_, j) => j !== i))}>×</Button>
            ) : null}
          </div>
        ))}
        {rows.length < 10 ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setRows((p) => [...p, emptyField()])}>Add field</Button>
        ) : null}
      </div>

      {!isCreate ? (
        <div className="space-y-2">
          <Label>Reference image</Label>
          <ArtworkThumbnail templateId={templateId} bust={bust} />
          <input
            type="file"
            accept="image/*"
            aria-label="Upload reference image"
            onChange={(e) => void onPickImage(e.target.files?.[0])}
          />
        </div>
      ) : null}

      <div className="flex gap-2">
        <Button onClick={() => void submit()} disabled={!canSubmit || create.isPending || update.isPending}>
          {isCreate ? "Create template" : "Save"}
        </Button>
        <Button variant="outline" onClick={() => void navigate("/admin/artwork-templates")}>Cancel</Button>
        {!isCreate && has("artwork-templates:write") ? (
          <Button variant="destructive" onClick={() => void handleDelete()} disabled={del.isPending}>Delete</Button>
        ) : null}
      </div>
    </div>
  );
}
